import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import templateImg from "../../Assets/pdfImgaes/orderInvoice.png";

export const generateInvoicePDF = (order) => {
  const doc = new jsPDF("p", "mm", "a4");

  // background
  doc.addImage(templateImg, "PNG", 0, 0, 210, 297);

  // ==========================
  // BILL TO
  // ==========================

  doc.setFontSize(10);

  // Helper: print wrapped text and return new Y position
  const printWrappedText = (
    doc,
    text,
    x,
    y,
    maxWidth,
    fontSize = 10,
    lineHeight = 6,
  ) => {
    doc.setFontSize(fontSize);
    const lines = doc.splitTextToSize(text, maxWidth);
    lines.forEach((line, i) => {
      doc.text(line, x, y + i * lineHeight);
    });
    return y + lines.length * lineHeight; // new Y after all lines
  };

  const capitalizeWords = (str) =>
    str.replace(/\b\w/g, (char) => char.toUpperCase());

  let currentY = 50;

  // Name
  doc.text(order.userName || "-", 30, currentY);
  currentY += 8;

  // Company (Bank name)
  doc.text(order.companyId?.bankName || "-", 30, currentY);
  currentY += 8;

  // Address (wrapped)
  const addressText = capitalizeWords(order.address || "-");

  // Wrap the address
  const addressLines = doc.splitTextToSize(addressText, 100); // adjust width

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  const lineHeight = 5.5;

  // Print each line
  addressLines.forEach((line, index) => {
    doc.text(line, 30, currentY + index * lineHeight);
  });

  // Move GST below the last address line
  currentY += addressLines.length * lineHeight + 3;

  // GST
  doc.text(order.companyId?.gstno || "-", 30, 80);

  // ==========================
  // RIGHT SIDE
  // ==========================

  doc.text(order.invoiceNumber || "-", 165, 50);

  doc.text(new Date(order.createdAt).toLocaleDateString("en-GB"), 165, 57.5);

  doc.text(order.sellerCompanyId?.pan || "-", 165, 65);

  doc.text(order.sellerCompanyId?.gst || "-", 165, 72);

  const customerId = order.companyId?.customer_id || "-";
  doc.text(customerId, 165, 79);

  // ==========================
  // PRODUCT TABLE
  // ==========================

  const rows =
    order.products?.map((item, index) => {
      const taxable = item.quantity * item.pricePerUnit;

      const cgst = taxable * ((item.cgstPercentage || 0) / 100);

      const sgst = taxable * ((item.sgstPercentage || 0) / 100);

      const total = taxable + cgst + sgst;

      return [
        index + 1,
        item.productId?.hsnCode || "",
        item.productId?.name || "",
        item.size || "-",
        item.productId?.type === "withVariation" ? item.quantity : "-",
        item.pricePerUnit,
        taxable.toFixed(2),
        cgst.toFixed(2),
        sgst.toFixed(2),
        total.toFixed(2),
      ];
    }) || [];
  autoTable(doc, {
    startY: 110,
    body: rows,

    styles: {
      fontSize: 8,
      cellPadding: 1.5,
      minCellHeight: 8,
      valign: "middle",
      overflow: "linebreak",
    },

    columnStyles: {
      0: {
        cellWidth: 12,
        halign: "center",
      }, // Sl No
      1: {
        cellWidth: 22,
        halign: "center",
      }, // HSN
      2: {
        cellWidth: 25,
        halign: "center",
      }, // Material
      3: {
        cellWidth: 16,
        halign: "center",
      }, // Size
      4: {
        cellWidth: 16,
        halign: "center",
      }, // Quantity
      5: {
        cellWidth: 18,
        halign: "right",
      }, // Per Pc Rate
      6: {
        cellWidth: 22,
        halign: "right",
      }, // Sub Total
      7: {
        cellWidth: 20,
        halign: "right",
      }, // CGST
      8: {
        cellWidth: 20,
        halign: "right",
      }, // SGST
      9: {
        cellWidth: 28,
        halign: "center",
      }, // Total Amount
    },

    margin: {
      left: 6,
      right: 6,
    },
  });

  const finalY = doc.lastAutoTable.finalY || 200;
  if (order.note) {
    doc.setFontSize(10);
    doc.text("Note: " + order.note, 13, finalY + 70);
  }
  // ==========================
  // TOTAL
  // ==========================

  doc.setFontSize(12);
  doc.setFont(undefined, "bold");

  doc.text(`Rs ${order.grandTotal?.toLocaleString()}`, 170, 219);

  // ==========================
  // AMOUNT IN WORDS
  // ==========================

  function numberToWords(num) {
    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];

    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    function convertHundreds(n) {
      let str = "";

      if (n > 99) {
        str += ones[Math.floor(n / 100)] + " Hundred ";
        n %= 100;
      }

      if (n > 19) {
        str += tens[Math.floor(n / 10)] + " ";
        n %= 10;
      }

      if (n > 0) {
        str += ones[n] + " ";
      }

      return str.trim();
    }

    if (num === 0) return "Zero";

    let result = "";

    const crore = Math.floor(num / 10000000);
    num %= 10000000;

    const lakh = Math.floor(num / 100000);
    num %= 100000;

    const thousand = Math.floor(num / 1000);
    num %= 1000;

    if (crore) result += convertHundreds(crore) + " Crore ";
    if (lakh) result += convertHundreds(lakh) + " Lakh ";
    if (thousand) result += convertHundreds(thousand) + " Thousand ";
    if (num) result += convertHundreds(num);

    return result.trim();
  }

  doc.setFontSize(10);

  const amountInWords = `${numberToWords(Math.round(order.grandTotal || 0))} `;

  const wordLines = doc.splitTextToSize(amountInWords, 150);

  doc.text(wordLines, 23, 224);

  // ==========================
  // BANK DETAILS
  // ==========================

  doc.setFontSize(9);

  doc.text(order.selectedBankAccount?.accountNumber || "", 125, 253);

  doc.text(order.selectedBankAccount?.ifscCode || "", 34, 258);

  doc.text(order.selectedBankAccount?.branchName || "", 84, 258);

  doc.text(order.selectedBankAccount?.branchAddress || "", 136, 258);

  // ==========================
  // queryContact
  // ==========================

  doc.setFontSize(9);

  doc.setTextColor(255, 0, 0);

  doc.text(order.selectedQueryContact?.contactPersonName || "", 33, 276);
  doc.text(order.selectedQueryContact?.phone || "", 95, 276);

  doc.save(`${order.invoiceNumber}.pdf`);
};
